// Client-side social engagement store for the Experience / Gallery feature.
//
// Persists likes, comments, and user-uploaded photos to localStorage so the UI
// stays interactive without a backend. Designed to be swapped for a real API
// later — every function returns/accepts the same shape a backend would emit.

const NS = "ffl.social.v1";

const safeRead = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const safeWrite = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore — quota or private mode
  }
};

const subscribers = new Set();
export const subscribe = (fn) => {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
};
const notify = () => subscribers.forEach((f) => f());

// ----- Likes -------------------------------------------------------------------
const LIKES_KEY = `${NS}.likes`;       // { [entryId]: number }
const LIKED_KEY = `${NS}.liked`;       // { [entryId]: true } — current user

export const getLikes = (entryId) => {
  const all = safeRead(LIKES_KEY, {});
  const liked = safeRead(LIKED_KEY, {});
  return { count: all[entryId] || 0, liked: !!liked[entryId] };
};

export const toggleLike = (entryId) => {
  const all = safeRead(LIKES_KEY, {});
  const liked = safeRead(LIKED_KEY, {});
  const isLiked = !!liked[entryId];
  if (isLiked) {
    all[entryId] = Math.max(0, (all[entryId] || 1) - 1);
    delete liked[entryId];
  } else {
    all[entryId] = (all[entryId] || 0) + 1;
    liked[entryId] = true;
  }
  safeWrite(LIKES_KEY, all);
  safeWrite(LIKED_KEY, liked);
  notify();
  return { count: all[entryId], liked: !isLiked };
};

// ----- Comments ----------------------------------------------------------------
const COMMENTS_KEY = `${NS}.comments`; // { [entryId]: Comment[] }
// Comment shape: { id, entryId, name, rating (1-5), text, createdAt (iso), pending: true }

export const getComments = (entryId) => {
  const all = safeRead(COMMENTS_KEY, {});
  return (all[entryId] || []).slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

export const addComment = (entryId, { name, rating, text }) => {
  const all = safeRead(COMMENTS_KEY, {});
  const list = all[entryId] || [];
  const comment = {
    id: `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    entryId,
    name: (name || "Guest").trim().slice(0, 40),
    rating: Math.max(1, Math.min(5, Number(rating) || 5)),
    text: (text || "").trim().slice(0, 600),
    createdAt: new Date().toISOString(),
    pending: true, // moderation flag — UI shows "awaiting review"
  };
  all[entryId] = [comment, ...list];
  safeWrite(COMMENTS_KEY, all);
  notify();
  return comment;
};

// ----- User-uploaded photos ----------------------------------------------------
const PHOTOS_KEY = `${NS}.photos`;     // { [entryId]: UserPhoto[] }
// UserPhoto: { id, entryId, name, dataUrl, createdAt, pending: true }

export const getUserPhotos = (entryId) => {
  const all = safeRead(PHOTOS_KEY, {});
  return (all[entryId] || []).slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

export const addUserPhoto = (entryId, { name, dataUrl }) => {
  const all = safeRead(PHOTOS_KEY, {});
  const list = all[entryId] || [];
  const photo = {
    id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    entryId,
    name: (name || "Guest").trim().slice(0, 40),
    dataUrl,
    createdAt: new Date().toISOString(),
    pending: true,
  };
  all[entryId] = [photo, ...list];
  safeWrite(PHOTOS_KEY, all);
  notify();
  return photo;
};

// ----- Formatters --------------------------------------------------------------
export const formatRelativeTime = (iso) => {
  const t = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - t) / 1000);
  if (diffSec < 60) return "just now";
  const m = Math.round(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const formatEntryDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export const initials = (name) =>
  (name || "G")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");
